import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useNavigate, Link } from 'react-router-dom';
import { Layout } from '../../components/Layout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../../components/ui/tooltip';
import { toast } from 'sonner';
import GridLayout from 'react-grid-layout';
import 'react-grid-layout/css/styles.css';
import {
  MapPin, FileText, Calendar, Users, TrendingUp, Clock, CheckCircle, 
  AlertCircle, Megaphone, Star, Target, ArrowRight, Plus, 
  Camera, PenTool, Send, Building2, Sparkles, Eye, BarChart3,
  CalendarDays, ListChecks, MessageSquare, ExternalLink, Settings,
  FolderOpen, TrendingDown, Minus, DollarSign, Image, Gauge, Lock,
  GripVertical, Unlock, RotateCcw
} from 'lucide-react';
import { usePermissions, FEATURE_PERMISSIONS } from '../../components/common/PermissionButton';
import { PendingApprovalsWidget } from '../../components/marketing/PendingApprovalsWidget';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// KPI Card Component with glow effect
const KPICard = ({ title, value, subtitle, icon: Icon, color = "blue", trend, onClick }) => {
  const colorClasses = {
    blue: "border-blue-500 dark:border-blue-400",
    emerald: "border-emerald-500 dark:border-emerald-400",
    purple: "border-purple-500 dark:border-purple-400",
    orange: "border-orange-500 dark:border-orange-400",
    rose: "border-rose-500 dark:border-rose-400",
    yellow: "border-yellow-500 dark:border-yellow-400"
  };

  const iconColorClasses = {
    blue: "text-blue-500 dark:text-blue-400",
    emerald: "text-emerald-500 dark:text-emerald-400",
    purple: "text-purple-500 dark:text-purple-400",
    orange: "text-orange-500 dark:text-orange-400",
    rose: "text-rose-500 dark:text-rose-400",
    yellow: "text-yellow-500 dark:text-yellow-400"
  };

  const glowStyles = {
    blue: '0 0 20px rgba(59, 130, 246, 0.3), 0 0 40px rgba(59, 130, 246, 0.1)',
    emerald: '0 0 20px rgba(16, 185, 129, 0.3), 0 0 40px rgba(16, 185, 129, 0.1)',
    purple: '0 0 20px rgba(168, 85, 247, 0.3), 0 0 40px rgba(168, 85, 247, 0.1)',
    orange: '0 0 20px rgba(249, 115, 22, 0.3), 0 0 40px rgba(249, 115, 22, 0.1)',
    rose: '0 0 20px rgba(244, 63, 94, 0.3), 0 0 40px rgba(244, 63, 94, 0.1)',
    yellow: '0 0 20px rgba(234, 179, 8, 0.3), 0 0 40px rgba(234, 179, 8, 0.1)'
  };

  return (
    <Card
      className={`dark:bg-slate-800/60 border-2 ${colorClasses[color]} transition-all duration-300 hover:scale-[1.02] cursor-pointer`}
      style={{ boxShadow: glowStyles[color] }}
      onClick={onClick}
    >
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-sm font-medium dark:text-slate-200">{title}</CardTitle>
        <Icon className={`h-4 w-4 ${iconColorClasses[color]}`} />
      </CardHeader>
      <CardContent>
        <div className="text-2xl font-bold dark:text-white">{value}</div>
        {subtitle && (
          <p className="text-xs text-muted-foreground dark:text-slate-400 mt-1">{subtitle}</p>
        )}
        {trend && (
          <div className={`flex items-center gap-1 mt-1 text-xs ${trend > 0 ? 'text-emerald-500' : 'text-rose-500'}`}>
            <TrendingUp className={`h-3 w-3 ${trend < 0 ? 'rotate-180' : ''}`} />
            {Math.abs(trend)}% vs last week
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// Activity Item Component
const ActivityItem = ({ activity }) => {
  const typeIcons = {
    check_in: Camera,
    blog_post: FileText,
    social_post: Send,
    campaign: Megaphone,
    approval: CheckCircle
  };

  const typeColors = {
    check_in: 'bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400',
    blog_post: 'bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400',
    social_post: 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400',
    campaign: 'bg-orange-100 dark:bg-orange-900/30 text-orange-600 dark:text-orange-400',
    approval: 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-600 dark:text-yellow-400'
  };

  const statusBadges = {
    draft: 'bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300',
    pending_approval: 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-400',
    approved: 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400',
    published: 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400',
    scheduled: 'bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-400'
  };

  const Icon = typeIcons[activity.type] || FileText;

  const formatTime = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  return (
    <div className="flex items-start gap-3 p-3 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-700/50 transition-colors">
      <div className={`p-2 rounded-lg ${typeColors[activity.type]}`}>
        <Icon className="h-4 w-4" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="font-medium text-sm dark:text-white truncate">{activity.title}</span>
          <Badge className={`text-xs ${statusBadges[activity.action] || statusBadges.draft}`}>
            {activity.action?.replace('_', ' ')}
          </Badge>
        </div>
        <p className="text-xs text-muted-foreground dark:text-slate-400 mt-0.5">
          {activity.user_name} • {formatTime(activity.timestamp)}
        </p>
      </div>
    </div>
  );
};

// Schedule Item Component
const ScheduleItem = ({ event }) => {
  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
  };

  return (
    <div className="flex items-center gap-3 p-2 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-700/50">
      <div className="w-12 h-12 rounded-lg bg-purple-100 dark:bg-purple-900/30 flex flex-col items-center justify-center">
        <span className="text-xs text-purple-600 dark:text-purple-400 font-medium">
          {new Date(event.start).toLocaleDateString('en-US', { month: 'short' })}
        </span>
        <span className="text-lg font-bold text-purple-600 dark:text-purple-400">
          {new Date(event.start).getDate()}
        </span>
      </div>
      <div className="flex-1">
        <p className="font-medium text-sm dark:text-white">{event.title}</p>
        <p className="text-xs text-muted-foreground dark:text-slate-400">
          {formatDate(event.start)} • {event.channel || event.event_type}
        </p>
      </div>
    </div>
  );
};

// Map Widget Placeholder
const MapWidget = ({ checkIns }) => {
  return (
    <Card className="dark:bg-slate-800/60 dark:border-slate-700">
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center gap-2 text-base dark:text-white">
          <MapPin className="h-4 w-4 text-blue-500" />
          Check-In Locations
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="h-32 bg-gradient-to-br from-blue-50 to-slate-100 dark:from-slate-700 dark:to-slate-800 rounded-lg flex items-center justify-center border border-dashed border-slate-300 dark:border-slate-600">
          <div className="text-center">
            <MapPin className="h-8 w-8 text-slate-400 dark:text-slate-500 mx-auto mb-1" />
            <p className="text-xs text-muted-foreground dark:text-slate-400">
              {checkIns || 0} check-ins this month
            </p>
            <Button variant="outline" size="sm" className="mt-2 text-xs h-7">
              <Plus className="h-3 w-3 mr-1" /> Add Check-In
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

// Quick Actions Component - Phase 2: Only Create Content + View Schedule
// Phase 3.5: Permission-aware
const QuickActions = ({ onAction, permissions }) => {
  const canCreateContent = permissions?.[FEATURE_PERMISSIONS.CONTENT_STUDIO_CREATE];
  const canViewSchedule = permissions?.[FEATURE_PERMISSIONS.SCHEDULER_EDIT] || permissions?.[FEATURE_PERMISSIONS.SCHEDULER_CREATE] || true; // Always allow view

  const actions = [
    { 
      id: 'content_studio', 
      label: 'Create Content', 
      icon: PenTool, 
      color: 'bg-purple-500 hover:bg-purple-600',
      permission: FEATURE_PERMISSIONS.CONTENT_STUDIO_CREATE,
      hasPermission: canCreateContent
    },
    { 
      id: 'scheduler', 
      label: 'View Schedule', 
      icon: CalendarDays, 
      color: 'bg-blue-500 hover:bg-blue-600',
      permission: null,
      hasPermission: true // Always viewable
    }
  ];

  return (
    <div className="flex gap-2 flex-wrap items-center">
      {actions.map(action => (
        action.hasPermission ? (
          <Button
            key={action.id}
            className={`${action.color} text-white`}
            size="sm"
            onClick={() => onAction(action.id)}
            data-testid={`quick-action-${action.id}`}
          >
            <action.icon className="h-4 w-4 mr-1" />
            {action.label}
          </Button>
        ) : (
          <TooltipProvider key={action.id}>
            <Tooltip>
              <TooltipTrigger asChild>
                <span>
                  <Button
                    className={`${action.color} text-white opacity-50 cursor-not-allowed`}
                    size="sm"
                    disabled
                    data-testid={`quick-action-${action.id}`}
                  >
                    <Lock className="h-4 w-4 mr-1" />
                    {action.label}
                  </Button>
                </span>
              </TooltipTrigger>
              <TooltipContent>You do not have permission to {action.label.toLowerCase()}</TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )
      ))}
      {/* Settings Link */}
      <Link to="/marketing-hub/settings" className="ml-2">
        <Button variant="ghost" size="sm" data-testid="settings-link">
          <Settings className="h-4 w-4" />
        </Button>
      </Link>
    </div>
  );
};

// AI Suggestions Component
const AISuggestions = () => {
  const suggestions = [
    { text: "Create 3 check-ins in high-value ZIP codes this week", priority: "high" },
    { text: "Draft blog post about summer exterior protection", priority: "medium" },
    { text: "Schedule review request follow-ups for recent completions", priority: "medium" }
  ];

  return (
    <Card className="dark:bg-slate-800/60 dark:border-slate-700">
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center gap-2 text-base dark:text-white">
          <Sparkles className="h-4 w-4 text-purple-500" />
          AI Suggested Actions
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-2">
        {suggestions.map((suggestion, idx) => (
          <div
            key={idx}
            className="flex items-start gap-2 p-2 rounded-lg bg-purple-50/50 dark:bg-purple-900/10 hover:bg-purple-50 dark:hover:bg-purple-900/20 cursor-pointer transition-colors"
          >
            <Target className="h-4 w-4 text-purple-500 mt-0.5 flex-shrink-0" />
            <p className="text-sm dark:text-slate-200">{suggestion.text}</p>
          </div>
        ))}
      </CardContent>
    </Card>
  );
};

// =====================================
// PHASE 2 WIDGETS - Command Center
// =====================================

// Format helpers for widgets
const formatCurrency = (value) => {
  if (value === undefined || value === null) return '$0';
  if (value >= 1000000) return `$${(value / 1000000).toFixed(1)}M`;
  if (value >= 1000) return `$${(value / 1000).toFixed(1)}K`;
  return `$${Number(value).toFixed(0)}`;
};

const formatNumber = (value) => {
  if (value === undefined || value === null) return '0';
  if (value >= 1000) return `${(value / 1000).toFixed(1)}K`;
  return Number(value).toFixed(0);
};

// Status color mapping
const statusColors = {
  on_pace: { bg: 'bg-green-100 dark:bg-green-900/20', text: 'text-green-600 dark:text-green-400', badge: 'bg-green-500' },
  at_risk: { bg: 'bg-yellow-100 dark:bg-yellow-900/20', text: 'text-yellow-600 dark:text-yellow-400', badge: 'bg-yellow-500' },
  below_min: { bg: 'bg-red-100 dark:bg-red-900/20', text: 'text-red-600 dark:text-red-400', badge: 'bg-red-500' },
  green: { bg: 'bg-green-100 dark:bg-green-900/20', text: 'text-green-600 dark:text-green-400', badge: 'bg-green-500' },
  yellow: { bg: 'bg-yellow-100 dark:bg-yellow-900/20', text: 'text-yellow-600 dark:text-yellow-400', badge: 'bg-yellow-500' },
  red: { bg: 'bg-red-100 dark:bg-red-900/20', text: 'text-red-600 dark:text-red-400', badge: 'bg-red-500' }
};

// On-Track Pacing Widget - Highest Priority
const OnTrackPacingWidget = ({ navigate }) => {
  const [pacingData, setPacingData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchPacing = async () => {
      try {
        const token = localStorage.getItem('access_token') || localStorage.getItem('token');
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        const response = await axios.get(`${API}/marketing/pacing/realtime?period=mtd`, { headers });
        setPacingData(response.data);
      } catch (error) {
        console.error('Failed to fetch pacing data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchPacing();
  }, []);

  const getStatusLabel = (status) => {
    if (status === 'on_pace' || status === 'green') return 'On Track';
    if (status === 'at_risk' || status === 'yellow') return 'At Risk';
    if (status === 'below_min' || status === 'red') return 'Below Min';
    return 'Unknown';
  };

  const metrics = pacingData?.metrics || {};
  const leads = metrics.leads || {};
  const revenue = metrics.revenue || {};
  const sales = metrics.sales || {};

  return (
    <Card 
      className="dark:bg-slate-800/60 dark:border-slate-700 border-l-4 border-l-emerald-500 hover:shadow-lg transition-shadow cursor-pointer"
      onClick={() => navigate('/marketing-hub/pacing')}
      data-testid="pacing-widget"
    >
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center justify-between dark:text-white">
          <span className="flex items-center gap-2 text-base">
            <Gauge className="h-5 w-5 text-emerald-500" />
            On-Track Pacing
          </span>
          <Button variant="ghost" size="sm" className="text-xs" onClick={(e) => { e.stopPropagation(); navigate('/marketing-hub/pacing'); }}>
            Open <ArrowRight className="h-3 w-3 ml-1" />
          </Button>
        </CardTitle>
        <CardDescription className="dark:text-slate-400">MTD Performance vs Targets</CardDescription>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex items-center justify-center py-6">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-emerald-600"></div>
          </div>
        ) : (
          <div className="space-y-3">
            {/* Leads KPI */}
            <div className={`flex items-center justify-between p-2 rounded-lg ${statusColors[leads.status]?.bg || 'bg-slate-100 dark:bg-slate-700'}`}>
              <div className="flex items-center gap-2">
                <Users className="h-4 w-4 text-slate-500" />
                <span className="text-sm font-medium dark:text-white">Leads</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="font-bold dark:text-white">{formatNumber(leads.actual || 0)}</span>
                <span className="text-xs text-muted-foreground">/ {formatNumber(leads.target || 0)}</span>
                <span className={`w-2 h-2 rounded-full ${statusColors[leads.status]?.badge || 'bg-slate-400'}`} />
              </div>
            </div>
            
            {/* Sales KPI */}
            <div className={`flex items-center justify-between p-2 rounded-lg ${statusColors[sales.status]?.bg || 'bg-slate-100 dark:bg-slate-700'}`}>
              <div className="flex items-center gap-2">
                <Target className="h-4 w-4 text-slate-500" />
                <span className="text-sm font-medium dark:text-white">Sales</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="font-bold dark:text-white">{formatNumber(sales.actual || 0)}</span>
                <span className="text-xs text-muted-foreground">/ {formatNumber(sales.target || 0)}</span>
                <span className={`w-2 h-2 rounded-full ${statusColors[sales.status]?.badge || 'bg-slate-400'}`} />
              </div>
            </div>
            
            {/* Revenue KPI */}
            <div className={`flex items-center justify-between p-2 rounded-lg ${statusColors[revenue.status]?.bg || 'bg-slate-100 dark:bg-slate-700'}`}>
              <div className="flex items-center gap-2">
                <DollarSign className="h-4 w-4 text-slate-500" />
                <span className="text-sm font-medium dark:text-white">Revenue</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="font-bold dark:text-white">{formatCurrency(revenue.actual || 0)}</span>
                <span className="text-xs text-muted-foreground">/ {formatCurrency(revenue.target || 0)}</span>
                <span className={`w-2 h-2 rounded-full ${statusColors[revenue.status]?.badge || 'bg-slate-400'}`} />
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// Marketing Tracker Widget
const MarketingTrackerWidget = ({ navigate }) => {
  const [trackerData, setTrackerData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchTracker = async () => {
      try {
        const token = localStorage.getItem('access_token') || localStorage.getItem('token');
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        const response = await axios.get(`${API}/marketing/tracker/overview?date_range=last_30`, { headers });
        setTrackerData(response.data);
      } catch (error) {
        console.error('Failed to fetch tracker data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchTracker();
  }, []);

  return (
    <Card 
      className="dark:bg-slate-800/60 dark:border-slate-700 border-l-4 border-l-blue-500 hover:shadow-lg transition-shadow cursor-pointer"
      onClick={() => navigate('/marketing-hub/tracker')}
      data-testid="tracker-widget"
    >
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center justify-between dark:text-white">
          <span className="flex items-center gap-2 text-base">
            <BarChart3 className="h-5 w-5 text-blue-500" />
            Marketing Tracker
          </span>
          <Button variant="ghost" size="sm" className="text-xs" onClick={(e) => { e.stopPropagation(); navigate('/marketing-hub/tracker'); }}>
            Open <ArrowRight className="h-3 w-3 ml-1" />
          </Button>
        </CardTitle>
        <CardDescription className="dark:text-slate-400">Last 30 Days Attribution</CardDescription>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex items-center justify-center py-6">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
          </div>
        ) : (
          <div className="space-y-3">
            {/* Leads */}
            <div className="flex items-center justify-between p-2 rounded-lg bg-blue-50 dark:bg-blue-900/20">
              <div className="flex items-center gap-2">
                <Users className="h-4 w-4 text-blue-500" />
                <span className="text-sm font-medium dark:text-white">Total Leads</span>
              </div>
              <span className="font-bold text-blue-600 dark:text-blue-400">{formatNumber(trackerData?.leads || 0)}</span>
            </div>
            
            {/* Schedules */}
            <div className="flex items-center justify-between p-2 rounded-lg bg-purple-50 dark:bg-purple-900/20">
              <div className="flex items-center gap-2">
                <Calendar className="h-4 w-4 text-purple-500" />
                <span className="text-sm font-medium dark:text-white">Schedules</span>
              </div>
              <span className="font-bold text-purple-600 dark:text-purple-400">{formatNumber(trackerData?.schedules || 0)}</span>
            </div>
            
            {/* Purchases */}
            <div className="flex items-center justify-between p-2 rounded-lg bg-emerald-50 dark:bg-emerald-900/20">
              <div className="flex items-center gap-2">
                <CheckCircle className="h-4 w-4 text-emerald-500" />
                <span className="text-sm font-medium dark:text-white">Purchases</span>
              </div>
              <span className="font-bold text-emerald-600 dark:text-emerald-400">{formatNumber(trackerData?.purchases || 0)}</span>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// Ads Manager Widget - Campaign Performance Overview
const AdsManagerWidget = ({ navigate }) => {
  const [adsData, setAdsData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchAds = async () => {
      try {
        const token = localStorage.getItem('access_token') || localStorage.getItem('token');
        const now = new Date();
        const start = new Date(now.getFullYear(), now.getMonth(), 1).toISOString().split('T')[0];
        const end = now.toISOString().split('T')[0];
        const response = await axios.get(
          `${API}/marketing/ads/report/demo`,
          { params: { level: 'campaign', start_date: start, end_date: end }, headers: { Authorization: `Bearer ${token}` } }
        );
        setAdsData(response.data);
      } catch (error) {
        console.error('Failed to fetch ads data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchAds();
  }, []);

  const totals = adsData?.totals || {};
  const campaigns = adsData?.data || [];
  const activeCampaigns = campaigns.filter(c => c.status === 'active').length;
  const pausedCampaigns = campaigns.filter(c => c.status === 'paused').length;

  return (
    <Card 
      className="dark:bg-slate-800/60 dark:border-slate-700 border-l-4 border-l-orange-500 hover:shadow-lg transition-shadow cursor-pointer"
      onClick={() => navigate('/marketing-hub/ads-manager')}
      data-testid="ads-manager-widget"
    >
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center justify-between dark:text-white">
          <span className="flex items-center gap-2 text-base">
            <Megaphone className="h-5 w-5 text-orange-500" />
            Ads Manager
          </span>
          <Button variant="ghost" size="sm" className="text-xs" onClick={(e) => { e.stopPropagation(); navigate('/marketing-hub/ads-manager'); }}>
            Open <ArrowRight className="h-3 w-3 ml-1" />
          </Button>
        </CardTitle>
        <CardDescription className="dark:text-slate-400">
          Campaign Performance
          {campaigns.length > 0 && <Badge variant="outline" className="ml-2 text-amber-500 border-amber-500 text-[9px]">Demo</Badge>}
        </CardDescription>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex items-center justify-center py-6">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-orange-600"></div>
          </div>
        ) : (
          <div className="space-y-3">
            {/* Campaign counts */}
            <div className="flex items-center justify-between p-2 rounded-lg bg-orange-50 dark:bg-orange-900/20">
              <span className="text-sm font-medium dark:text-white">Campaigns</span>
              <div className="flex items-center gap-2 text-xs">
                <Badge variant="default" className="bg-green-500 text-[10px] px-1">{activeCampaigns} active</Badge>
                {pausedCampaigns > 0 && <Badge variant="secondary" className="text-[10px] px-1">{pausedCampaigns} paused</Badge>}
              </div>
            </div>
            
            {/* Spend */}
            <div className="flex items-center justify-between p-2 rounded-lg bg-slate-50 dark:bg-slate-700/50">
              <div className="flex items-center gap-2">
                <DollarSign className="h-4 w-4 text-slate-500" />
                <span className="text-sm font-medium dark:text-white">Total Spend</span>
              </div>
              <span className="font-bold dark:text-white">{formatCurrency(totals.spend || 0)}</span>
            </div>
            
            {/* ROAS */}
            <div className="flex items-center justify-between p-2 rounded-lg bg-slate-50 dark:bg-slate-700/50">
              <div className="flex items-center gap-2">
                <TrendingUp className="h-4 w-4 text-emerald-500" />
                <span className="text-sm font-medium dark:text-white">ROAS</span>
              </div>
              <span className={`font-bold ${(totals.roas || 0) >= 6.66 ? 'text-emerald-600 dark:text-emerald-400' : 'text-yellow-600 dark:text-yellow-400'}`}>
                {(totals.roas || 0).toFixed(1)}x
              </span>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// Ad Spend Summary Widget - Quick view of spend by market  
const AdSpendWidget = ({ navigate }) => {
  const [spendData, setSpendData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchSpend = async () => {
      try {
        const token = localStorage.getItem('access_token') || localStorage.getItem('token');
        const response = await axios.get(
          `${API}/marketing/ad-spend/campaigns?period=mtd`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setSpendData(response.data);
      } catch (error) {
        console.error('Failed to fetch ad spend:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchSpend();
  }, []);

  const gt = spendData?.grand_totals || {};
  const marketSpend = spendData?.market_spend || {};
  const topMarkets = Object.entries(marketSpend)
    .sort(([,a], [,b]) => b.spend - a.spend)
    .slice(0, 4);

  return (
    <Card 
      className="dark:bg-slate-800/60 dark:border-slate-700 border-l-4 border-l-violet-500 hover:shadow-lg transition-shadow cursor-pointer"
      onClick={() => navigate('/marketing-hub/tracker')}
      data-testid="ad-spend-widget"
    >
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center justify-between dark:text-white">
          <span className="flex items-center gap-2 text-base">
            <DollarSign className="h-5 w-5 text-violet-500" />
            Ad Spend by Market
          </span>
          <Button variant="ghost" size="sm" className="text-xs" onClick={(e) => { e.stopPropagation(); navigate('/marketing-hub/tracker'); }}>
            Details <ArrowRight className="h-3 w-3 ml-1" />
          </Button>
        </CardTitle>
        <CardDescription className="dark:text-slate-400">
          MTD: {formatCurrency(gt.spend || 0)} total • CPL: {formatCurrency(gt.cpl || 0)}
        </CardDescription>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex items-center justify-center py-6">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-violet-600"></div>
          </div>
        ) : topMarkets.length > 0 ? (
          <div className="space-y-2">
            {topMarkets.map(([market, data]) => {
              const pct = gt.spend > 0 ? (data.spend / gt.spend * 100) : 0;
              return (
                <div key={market} className="space-y-1">
                  <div className="flex items-center justify-between text-xs">
                    <span className="font-medium dark:text-white capitalize">{market.replace('-', ' ')}</span>
                    <span className="text-muted-foreground">{formatCurrency(data.spend)} ({pct.toFixed(0)}%)</span>
                  </div>
                  <div className="h-2 bg-slate-200 dark:bg-slate-700 rounded-full overflow-hidden">
                    <div className="h-full bg-violet-500 rounded-full" style={{ width: `${pct}%` }} />
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="text-center py-4 text-sm text-muted-foreground">
            No ad spend data yet
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// Assets Widget - Connected to Backend (Phase 5)
const AssetsWidget = ({ navigate }) => {
  const [assetSummary, setAssetSummary] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchAssets = async () => {
      try {
        const token = localStorage.getItem('access_token') || localStorage.getItem('token');
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        const response = await axios.get(`${API}/assets/summary`, { headers });
        setAssetSummary(response.data);
      } catch (error) {
        console.error('Failed to fetch assets summary:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchAssets();
  }, []);

  return (
    <Card 
      className="dark:bg-slate-800/60 dark:border-slate-700 border-l-4 border-l-orange-500 hover:shadow-lg transition-shadow cursor-pointer"
      onClick={() => navigate('/marketing-hub/assets')}
      data-testid="assets-widget"
    >
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center justify-between dark:text-white">
          <span className="flex items-center gap-2 text-base">
            <FolderOpen className="h-5 w-5 text-orange-500" />
            Assets Library
          </span>
          <Button variant="ghost" size="sm" className="text-xs" onClick={(e) => { e.stopPropagation(); navigate('/marketing-hub/assets'); }}>
            Open <ArrowRight className="h-3 w-3 ml-1" />
          </Button>
        </CardTitle>
        <CardDescription className="dark:text-slate-400">Digital Asset Management</CardDescription>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex items-center justify-center py-6">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-orange-600"></div>
          </div>
        ) : (
          <div className="space-y-3">
            {/* Total Assets */}
            <div className="flex items-center justify-between p-2 rounded-lg bg-slate-50 dark:bg-slate-700/50">
              <div className="flex items-center gap-2">
                <FolderOpen className="h-4 w-4 text-slate-500" />
                <span className="text-sm font-medium dark:text-white">Total Assets</span>
              </div>
              <span className="font-bold dark:text-white">{formatNumber(assetSummary?.total_count || 0)}</span>
            </div>
            
            {/* Images */}
            <div className="flex items-center justify-between p-2 rounded-lg bg-blue-50 dark:bg-blue-900/20">
              <div className="flex items-center gap-2">
                <Image className="h-4 w-4 text-blue-500" />
                <span className="text-sm font-medium dark:text-white">Images</span>
              </div>
              <span className="font-bold text-blue-600 dark:text-blue-400">{formatNumber(assetSummary?.images_count || 0)}</span>
            </div>
            
            {/* Documents */}
            <div className="flex items-center justify-between p-2 rounded-lg bg-purple-50 dark:bg-purple-900/20">
              <div className="flex items-center gap-2">
                <FileText className="h-4 w-4 text-purple-500" />
                <span className="text-sm font-medium dark:text-white">Documents</span>
              </div>
              <span className="font-bold text-purple-600 dark:text-purple-400">{formatNumber(assetSummary?.documents_count || 0)}</span>
            </div>
            
            {/* Recent Uploads */}
            {assetSummary?.recent_assets?.length > 0 && (
              <div className="pt-2 border-t dark:border-slate-600">
                <p className="text-xs text-muted-foreground mb-2">Recent uploads</p>
                <div className="space-y-1">
                  {assetSummary.recent_assets.slice(0, 3).map((asset, idx) => (
                    <div key={idx} className="flex items-center gap-2 text-xs">
                      <div className="w-6 h-6 rounded bg-slate-100 dark:bg-slate-700 overflow-hidden flex-shrink-0">
                        {asset.asset_type === 'image' && asset.url ? (
                          <img 
                            src={`${BACKEND_URL}${asset.url}`} 
                            alt={asset.name} 
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <FileText className="w-4 h-4 m-1 text-slate-400" />
                        )}
                      </div>
                      <span className="truncate dark:text-slate-300">{asset.name}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// Quick Links Section - All Deployed Marketing Pages
const QuickLinksSection = ({ navigate }) => {
  const deployedPages = [
    { path: '/marketing-hub/dashboard', label: 'Dashboard', icon: BarChart3, color: 'text-blue-500' },
    { path: '/marketing-hub/tracker', label: 'Marketing Tracker', icon: TrendingUp, color: 'text-blue-500' },
    { path: '/marketing-hub/pacing', label: 'On-Track Pacing', icon: Gauge, color: 'text-emerald-500' },
    { path: '/marketing-hub/ads-manager', label: 'Ads Manager', icon: Megaphone, color: 'text-orange-500' },
    { path: '/marketing-hub/content-studio', label: 'Content Studio', icon: PenTool, color: 'text-purple-500' },
    { path: '/marketing-hub/scheduler', label: 'Scheduler', icon: CalendarDays, color: 'text-indigo-500' },
    { path: '/marketing-hub/assets', label: 'Assets', icon: FolderOpen, color: 'text-orange-500' },
    { path: '/marketing-hub/settings', label: 'Settings', icon: Settings, color: 'text-slate-500' }
  ];

  return (
    <Card className="dark:bg-slate-800/60 dark:border-slate-700" data-testid="quick-links-section">
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center gap-2 text-base dark:text-white">
          <ExternalLink className="h-4 w-4 text-slate-500" />
          Quick Links
        </CardTitle>
        <CardDescription className="dark:text-slate-400">Jump to any marketing tool</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          {deployedPages.map((page) => (
            <Link 
              key={page.path} 
              to={page.path}
              className="flex items-center gap-2 p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
              data-testid={`quick-link-${page.label.toLowerCase().replace(/\s+/g, '-')}`}
            >
              <page.icon className={`h-4 w-4 ${page.color}`} />
              <span className="text-sm dark:text-white">{page.label}</span>
            </Link>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};

// Main Dashboard Component
export const MarketingDashboard = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [activity, setActivity] = useState([]);
  const [pendingApprovals, setPendingApprovals] = useState([]);
  const [upcomingSchedule, setUpcomingSchedule] = useState([]);

  // Grid layout state
  const defaultLayout = [
    { i: 'kpis', x: 0, y: 0, w: 12, h: 2, minH: 2, minW: 6 },
    { i: 'pacing', x: 0, y: 2, w: 4, h: 3, minH: 3, minW: 3 },
    { i: 'tracker', x: 4, y: 2, w: 4, h: 3, minH: 3, minW: 3 },
    { i: 'ads', x: 8, y: 2, w: 4, h: 3, minH: 3, minW: 3 },
    { i: 'spend', x: 0, y: 5, w: 4, h: 3, minH: 3, minW: 3 },
    { i: 'assets', x: 4, y: 5, w: 4, h: 3, minH: 3, minW: 3 },
    { i: 'approvals', x: 8, y: 5, w: 4, h: 3, minH: 3, minW: 3 },
    { i: 'quicklinks', x: 0, y: 8, w: 12, h: 2, minH: 2, minW: 6 },
    { i: 'schedule', x: 0, y: 10, w: 6, h: 3, minH: 2, minW: 4 },
    { i: 'activity', x: 6, y: 10, w: 6, h: 3, minH: 2, minW: 4 },
  ];
  const [isEditMode, setIsEditMode] = useState(false);
  const [gridLayout, setGridLayout] = useState(() => {
    try {
      const saved = localStorage.getItem('mktg_hub_layout');
      return saved ? JSON.parse(saved) : defaultLayout;
    } catch { return defaultLayout; }
  });

  // Phase 3.5: Permission checks for Quick Actions
  const permissions = usePermissions([
    FEATURE_PERMISSIONS.CONTENT_STUDIO_CREATE,
    FEATURE_PERMISSIONS.SCHEDULER_CREATE,
    FEATURE_PERMISSIONS.SCHEDULER_EDIT
  ]);

  const fetchDashboardData = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('access_token') || localStorage.getItem('token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};

      const [statsRes, activityRes, approvalsRes, scheduleRes] = await Promise.all([
        axios.get(`${API}/marketing/dashboard/stats`, { headers }).catch(() => ({ data: null })),
        axios.get(`${API}/marketing/dashboard/activity?limit=10`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/marketing/dashboard/pending-approvals`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/marketing/dashboard/upcoming-schedule?days=7`, { headers }).catch(() => ({ data: [] }))
      ]);

      setStats(statsRes.data);
      setActivity(activityRes.data || []);
      setPendingApprovals(approvalsRes.data || []);
      setUpcomingSchedule(scheduleRes.data || []);
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
      toast.error('Failed to load marketing dashboard');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData]);

  const handleQuickAction = (actionId) => {
    switch (actionId) {
      case 'content_studio':
        navigate('/marketing-hub/content-studio');
        break;
      case 'scheduler':
        navigate('/marketing-hub/scheduler');
        break;
      case 'check_in':
        navigate('/marketing-hub/check-ins?action=create');
        break;
      case 'blog_post':
        navigate('/marketing-hub/content-studio?tab=blog&action=create');
        break;
      case 'social_post':
        navigate('/marketing-hub/content-studio?tab=social&action=create');
        break;
      case 'campaign':
        navigate('/marketing-hub/campaigns?action=create');
        break;
      default:
        break;
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-screen">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-4 text-muted-foreground dark:text-slate-400">Loading marketing dashboard...</p>
          </div>
        </div>
      </Layout>
    );
  }

  // Default stats if API returns null
  const displayStats = stats || {
    check_ins: { total: 0, this_week: 0, pending_approval: 0 },
    content: { blog_posts: 0, social_posts: 0, scheduled_next_7_days: 0 },
    campaigns: { active: 0 },
    agency: { open_requests: 0 },
    approvals: { total_pending: 0 },
    upcoming_scheduled: 0,
    reviews_requested: 0
  };

  return (
    <Layout>
      <div className="p-6 space-y-6" data-testid="marketing-dashboard">
        {/* Header with Quick Actions */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-foreground dark:text-white flex items-center gap-2">
              <Megaphone className="h-8 w-8 text-blue-500" />
              Marketing Hub
            </h1>
            <p className="text-muted-foreground dark:text-slate-400 mt-1">
              Command center for all marketing operations
            </p>
          </div>
          <QuickActions onAction={handleQuickAction} permissions={permissions} />
          
          {/* Layout Controls */}
          <div className="flex items-center gap-2">
            <Button
              variant={isEditMode ? 'default' : 'outline'}
              size="sm"
              onClick={() => {
                if (isEditMode) {
                  localStorage.setItem('mktg_hub_layout', JSON.stringify(gridLayout));
                  toast.success('Layout saved');
                }
                setIsEditMode(!isEditMode);
              }}
              className="gap-1"
            >
              {isEditMode ? <><Lock className="h-3 w-3" /> Lock Layout</> : <><Unlock className="h-3 w-3" /> Edit Layout</>}
            </Button>
            {isEditMode && (
              <Button variant="outline" size="sm" className="gap-1" onClick={() => { setGridLayout(defaultLayout); localStorage.removeItem('mktg_hub_layout'); toast.success('Layout reset'); }}>
                <RotateCcw className="h-3 w-3" /> Reset
              </Button>
            )}
          </div>
        </div>

        <GridLayout
          className="layout"
          layout={gridLayout}
          cols={12}
          rowHeight={80}
          width={1340}
          isDraggable={isEditMode}
          isResizable={isEditMode}
          onLayoutChange={(l) => setGridLayout(l)}
          draggableHandle=".grid-drag-handle"
          compactType="vertical"
          margin={[16, 16]}
        >
          <div key="kpis">
            <Card className={`h-full dark:bg-slate-800/60 dark:border-slate-700 overflow-auto ${isEditMode ? 'ring-2 ring-primary/20' : ''}`}>
              {isEditMode && <div className="grid-drag-handle cursor-move p-1 text-center"><GripVertical className="h-4 w-4 mx-auto text-muted-foreground" /></div>}
              <CardContent className="p-3">
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
                  <KPICard title="Check-Ins" value={displayStats.check_ins?.this_week || 0} subtitle="This week" icon={Camera} color="blue" />
                  <KPICard title="Scheduled" value={displayStats.content?.scheduled_next_7_days || 0} subtitle="Next 7 days" icon={CalendarDays} color="purple" />
                  <KPICard title="Campaigns" value={displayStats.campaigns?.active || 0} subtitle="Running" icon={Megaphone} color="emerald" />
                  <KPICard title="Reviews" value={displayStats.reviews_requested || 0} subtitle="Requested" icon={Star} color="orange" />
                  <KPICard title="Agency" value={displayStats.agency?.open_requests || 0} subtitle="Open" icon={Building2} color="blue" />
                  <KPICard title="Approvals" value={displayStats.approvals?.total_pending || 0} subtitle="Pending" icon={Clock} color="rose" />
                </div>
              </CardContent>
            </Card>
          </div>
          <div key="pacing" className="relative">
            {isEditMode && <div className="grid-drag-handle cursor-move p-1 text-center absolute top-0 left-0 right-0 z-10 bg-primary/10 rounded-t-lg"><GripVertical className="h-4 w-4 mx-auto text-muted-foreground" /></div>}
            <div className="h-full overflow-auto"><OnTrackPacingWidget navigate={navigate} /></div>
          </div>
          <div key="tracker" className="relative">
            {isEditMode && <div className="grid-drag-handle cursor-move p-1 text-center absolute top-0 left-0 right-0 z-10 bg-primary/10 rounded-t-lg"><GripVertical className="h-4 w-4 mx-auto text-muted-foreground" /></div>}
            <div className="h-full overflow-auto"><MarketingTrackerWidget navigate={navigate} /></div>
          </div>
          <div key="ads" className="relative">
            {isEditMode && <div className="grid-drag-handle cursor-move p-1 text-center absolute top-0 left-0 right-0 z-10 bg-primary/10 rounded-t-lg"><GripVertical className="h-4 w-4 mx-auto text-muted-foreground" /></div>}
            <div className="h-full overflow-auto"><AdsManagerWidget navigate={navigate} /></div>
          </div>
          <div key="spend" className="relative">
            {isEditMode && <div className="grid-drag-handle cursor-move p-1 text-center absolute top-0 left-0 right-0 z-10 bg-primary/10 rounded-t-lg"><GripVertical className="h-4 w-4 mx-auto text-muted-foreground" /></div>}
            <div className="h-full overflow-auto"><AdSpendWidget navigate={navigate} /></div>
          </div>
          <div key="assets" className="relative">
            {isEditMode && <div className="grid-drag-handle cursor-move p-1 text-center absolute top-0 left-0 right-0 z-10 bg-primary/10 rounded-t-lg"><GripVertical className="h-4 w-4 mx-auto text-muted-foreground" /></div>}
            <div className="h-full overflow-auto"><AssetsWidget navigate={navigate} /></div>
          </div>
          <div key="approvals" className="relative">
            {isEditMode && <div className="grid-drag-handle cursor-move p-1 text-center absolute top-0 left-0 right-0 z-10 bg-primary/10 rounded-t-lg"><GripVertical className="h-4 w-4 mx-auto text-muted-foreground" /></div>}
            <div className="h-full overflow-auto"><PendingApprovalsWidget /></div>
          </div>
          <div key="quicklinks" className="relative">
            {isEditMode && <div className="grid-drag-handle cursor-move p-1 text-center absolute top-0 left-0 right-0 z-10 bg-primary/10 rounded-t-lg"><GripVertical className="h-4 w-4 mx-auto text-muted-foreground" /></div>}
            <div className="h-full overflow-auto"><QuickLinksSection navigate={navigate} /></div>
          </div>
          <div key="schedule">
            <Card className={`h-full dark:bg-slate-800/60 dark:border-slate-700 overflow-auto ${isEditMode ? 'ring-2 ring-primary/20' : ''}`}>
              {isEditMode && <div className="grid-drag-handle cursor-move p-1 text-center"><GripVertical className="h-4 w-4 mx-auto text-muted-foreground" /></div>}
              <CardHeader className="pb-2">
                <CardTitle className="flex items-center justify-between dark:text-white text-base">
                  <span className="flex items-center gap-2"><Calendar className="h-4 w-4 text-purple-500" /> Schedule</span>
                  <Button variant="ghost" size="sm" onClick={() => navigate('/marketing-hub/scheduler')}><ExternalLink className="h-4 w-4" /></Button>
                </CardTitle>
              </CardHeader>
              <CardContent>
                {upcomingSchedule.length > 0 ? (
                  <div className="space-y-2">{upcomingSchedule.slice(0, 5).map((event, idx) => (<ScheduleItem key={idx} event={event} />))}</div>
                ) : (
                  <div className="text-center py-3 text-muted-foreground"><Calendar className="h-5 w-5 mx-auto mb-1 opacity-50" /><p className="text-xs">No upcoming items</p></div>
                )}
              </CardContent>
            </Card>
          </div>
          <div key="activity">
            <Card className={`h-full dark:bg-slate-800/60 dark:border-slate-700 overflow-auto ${isEditMode ? 'ring-2 ring-primary/20' : ''}`}>
              {isEditMode && <div className="grid-drag-handle cursor-move p-1 text-center"><GripVertical className="h-4 w-4 mx-auto text-muted-foreground" /></div>}
              <CardHeader className="pb-2">
                <CardTitle className="flex items-center gap-2 text-base dark:text-white"><ListChecks className="h-4 w-4 text-blue-500" /> Activity</CardTitle>
              </CardHeader>
              <CardContent>
                {activity.length > 0 ? (
                  <div className="space-y-1">{activity.map((item, idx) => (<ActivityItem key={idx} activity={item} />))}</div>
                ) : (
                  <div className="text-center py-3 text-muted-foreground"><MessageSquare className="h-5 w-5 mx-auto mb-1 opacity-50" /><p className="text-xs">No recent activity</p></div>
                )}
              </CardContent>
            </Card>
          </div>
        </GridLayout>
      </div>
    </Layout>
  );
};
